import { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Badge,
  Container,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Popover,
  ButtonBase,
  Button,
  MenuItem,
  TextField,
  Divider,
  Alert,
  InputAdornment,
  Stack,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import LogoutIcon from "@mui/icons-material/Logout";
import LockResetIcon from "@mui/icons-material/LockReset";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";

const SECTION_PROFILE = "profile";
const SECTION_DISPLAY_OPTIONS = "display-options";
const SECTION_NOTIFICATIONS = "notifications";
const SECTION_CHANGE_PASSWORD = "change-password";
const SECTION_SALESFORCE_IMPORT = "salesforce-import";
const NOTIFICATION_FREQUENCY_OPTIONS = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Biweekly" },
  { value: "TRIWEEKLY", label: "Triweekly" },
  { value: "MONTHLY", label: "Monthly" },
];
const MAX_DISPLAY_INITIALS_LENGTH = 3;
const AVATAR_IMAGE_SIZE = 256;
const MAX_PROFILE_IMAGE_DATA_LENGTH = 900000;
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const ACCEPTED_SALESFORCE_FILE_TYPES = ".xlsx,.xls";

const sectionOptions = [
  {
    id: SECTION_PROFILE,
    label: "Profile",
    icon: PersonOutlineIcon,
  },
  {
    id: SECTION_DISPLAY_OPTIONS,
    label: "Display Options",
    icon: PaletteOutlinedIcon,
  },
  {
    id: SECTION_NOTIFICATIONS,
    label: "Notification Frequency",
    icon: NotificationsNoneIcon,
  },
  {
    id: SECTION_CHANGE_PASSWORD,
    label: "Change Password",
    icon: LockResetIcon,
  },
  {
    id: SECTION_SALESFORCE_IMPORT,
    label: "Salesforce Import",
    icon: UploadFileOutlinedIcon,
    requiresSalesforceImport: true,
  },
];

const readOnlyFieldSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "#f3f4f6",
  },
  "& .MuiInputBase-input.Mui-disabled, & .MuiInputBase-input[readonly]": {
    WebkitTextFillColor: "#6b7280",
  },
};

function buildInitials(firstName, lastName) {
  return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "MB";
}

function buildAvatarInitials(displayInitials, fallbackFirstName, fallbackLastName) {
  const trimmedInitials = displayInitials?.trim();
  if (trimmedInitials) {
    return trimmedInitials.slice(0, MAX_DISPLAY_INITIALS_LENGTH).toUpperCase();
  }

  return buildInitials(fallbackFirstName, fallbackLastName);
}

function normalizeDisplayInitials(value) {
  return value.replace(/\s+/g, "").slice(0, MAX_DISPLAY_INITIALS_LENGTH).toUpperCase();
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read that image file."));
    };

    image.src = objectUrl;
  });
}

async function buildProfileImageDataUrl(file) {
  if (!file) {
    return null;
  }
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Upload a PNG, JPEG, WEBP, or GIF image.");
  }

  const image = await loadImageFromFile(file);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const sourceSize = Math.min(width, height);
  const sourceX = Math.max(0, (width - sourceSize) / 2);
  const sourceY = Math.max(0, (height - sourceSize) / 2);

  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_IMAGE_SIZE;
  canvas.height = AVATAR_IMAGE_SIZE;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Your browser could not prepare the uploaded image.");
  }

  context.clearRect(0, 0, AVATAR_IMAGE_SIZE, AVATAR_IMAGE_SIZE);
  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceSize,
    sourceSize,
    0,
    0,
    AVATAR_IMAGE_SIZE,
    AVATAR_IMAGE_SIZE
  );

  const dataUrl = canvas.toDataURL("image/webp", 0.86);
  if (dataUrl.length > MAX_PROFILE_IMAGE_DATA_LENGTH) {
    throw new Error("That image is too large after processing. Try a smaller image.");
  }

  return dataUrl;
}

function formatRole(roleOrRoles) {
  if (!roleOrRoles) return "";

  // Support both the new roles array and the legacy single role string
  const roles = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
  const roleLabelMap = {
    ADMIN: "Admin",
    USER: "User",
    BD_ADMIN: "BD Admin",
  };

  if (roles.includes("ADMIN")) return roleLabelMap.ADMIN;
  if (roles.includes("USER") && roles.length === 1) return roleLabelMap.USER;

  return roles
    .map((role) => {
      const normalizedRole = String(role || "").trim().toUpperCase();
      if (roleLabelMap[normalizedRole]) return roleLabelMap[normalizedRole];

      return String(role)
        .trim()
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    })
    .join(", ");
}

function formatCommentNotificationTimestamp(value) {
  if (!value) return "Unknown time";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-GB");
}

function formatCommentNotificationContext(value) {
  if (value === "CRUNCHBASE") return "Crunchbase";
  if (value === "GLOBALDATA") return "GlobalData";
  if (value === "COMPANIES_HOUSE") return "Company House";
  return "Company";
}

function getCommentNotificationContextChipSx(value) {
  if (value === "CRUNCHBASE") {
    return {
      color: "#166534",
      backgroundColor: "#f0fdf4",
      border: "1px solid rgba(34, 197, 94, 0.35)",
    };
  }

  if (value === "GLOBALDATA") {
    return {
      color: "#075985",
      backgroundColor: "#f0f9ff",
      border: "1px solid rgba(14, 165, 233, 0.28)",
    };
  }

  if (value === "COMPANIES_HOUSE") {
    return {
      color: "#0f766e",
      backgroundColor: "#ecfeff",
      border: "1px solid rgba(13, 148, 136, 0.35)",
    };
  }

  return {
    color: "#475569",
    backgroundColor: "#f8fafc",
    border: "1px solid rgba(148, 163, 184, 0.28)",
  };
}

function ProfileSection({
  currentUser,
}) {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <TextField
        size="small"
        label="First Name"
        value={currentUser?.first_name || ""}
        InputProps={{ readOnly: true }}
        sx={readOnlyFieldSx}
      />
      <TextField
        size="small"
        label="Surname"
        value={currentUser?.last_name || ""}
        InputProps={{ readOnly: true }}
        sx={readOnlyFieldSx}
      />
      <TextField
        size="small"
        label="Email Address"
        type="email"
        value={currentUser?.email || ""}
        InputProps={{ readOnly: true }}
        sx={readOnlyFieldSx}
      />
      <TextField
        size="small"
        label="Role"
        value={formatRole(currentUser?.roles ?? currentUser?.role)}
        InputProps={{ readOnly: true }}
        sx={readOnlyFieldSx}
      />
    </Box>
  );
}

function DisplayOptionsSection({ initials, profile, setProfile, onPhotoUpload, onRemovePhoto }) {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Alert severity="info" sx={{ alignItems: "center" }}>
        Use a photo, or show initials.
      </Alert>
      <TextField
        size="small"
        label="Display Initials"
        value={profile.displayInitials}
        onChange={(e) =>
          setProfile((prev) => ({
            ...prev,
            displayInitials: normalizeDisplayInitials(e.target.value),
          }))
        }
        helperText="Shown when no photo is set."
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "72px minmax(0, 1fr)",
          gap: 1.5,
          alignItems: "center",
          p: 1.5,
          borderRadius: 2,
          border: "1px solid rgba(148, 163, 184, 0.28)",
          backgroundColor: "#f8fafc",
        }}
      >
        <Avatar
          src={profile.userProfileImage || undefined}
          sx={{
            width: 64,
            height: 64,
            bgcolor: "#e64a19",
            fontSize: "1.2rem",
            fontWeight: 700,
          }}
        >
          {!profile.userProfileImage ? initials : null}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
            {profile.userProfileImage ? "Photo preview" : "Initials preview"}
          </Typography>
          <Typography sx={{ mt: 0.4, fontSize: "0.84rem", color: "#64748b" }}>
            {profile.userProfileImage ? "Saved in your profile." : "Used when no photo is set."}
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button
          component="label"
          variant="contained"
          sx={{ textTransform: "none" }}
        >
          Upload Photo
          <input
            hidden
            accept={ACCEPTED_IMAGE_TYPES.join(",")}
            type="file"
            onChange={onPhotoUpload}
          />
        </Button>
        <Button
          variant={profile.userProfileImage ? "outlined" : "text"}
          onClick={onRemovePhoto}
          sx={{ textTransform: "none" }}
        >
          Remove Photo
        </Button>
      </Stack>
    </Box>
  );
}

function NotificationsSection({ notifications, setNotifications }) {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <TextField
        size="small"
        label="Notification Email"
        type="email"
        value={notifications.email}
        InputProps={{ readOnly: true }}
        sx={readOnlyFieldSx}
      />
      <TextField
        size="small"
        select
        label="Frequency"
        value={notifications.frequency}
        onChange={(e) =>
          setNotifications((prev) => ({ ...prev, frequency: e.target.value }))
        }
      >
        {NOTIFICATION_FREQUENCY_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

function formatNumber(value) {
  return typeof value === "number" ? value.toLocaleString("en-GB") : value;
}

function SalesforceImportSection({ onSalesforceImport }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");

  const handleFileChange = (event) => {
    const [file] = event.target.files || [];
    setSelectedFile(file || null);
    setImportError("");
    setImportSuccess("");
    event.target.value = "";
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setImportError("Select a Salesforce workbook before importing.");
      return;
    }

    setImporting(true);
    setImportError("");
    setImportSuccess("");

    try {
      const payload = await onSalesforceImport?.(selectedFile);
      const years = Array.isArray(payload?.years) ? payload.years.join(", ") : "";
      setImportSuccess(
        `Imported ${formatNumber(payload?.importedCompanies || 0)} Salesforce accounts` +
          `${years ? ` for ${years}` : ""}. Refreshed ${formatNumber(payload?.refreshedMappings || 0)} company mappings.`
      );
      setSelectedFile(null);
    } catch (err) {
      setImportError(err?.message || "Unable to import the Salesforce workbook.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Alert severity="info" sx={{ alignItems: "center" }}>
        Upload the Salesforce won-contracts workbook. The import replaces the active
        Salesforce batch and refreshes client matching.
      </Alert>

      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: "1px solid rgba(148, 163, 184, 0.28)",
          backgroundColor: "#f8fafc",
          display: "grid",
          gap: 0.75,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
          {selectedFile ? selectedFile.name : "No workbook selected"}
        </Typography>
        <Typography sx={{ fontSize: "0.84rem", color: "#64748b" }}>
          {selectedFile
            ? `${formatNumber(selectedFile.size)} bytes`
            : "Accepted files: .xlsx or .xls"}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button component="label" variant="outlined" sx={{ textTransform: "none" }}>
          Select Workbook
          <input
            hidden
            accept={ACCEPTED_SALESFORCE_FILE_TYPES}
            type="file"
            onChange={handleFileChange}
          />
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!selectedFile || importing}
          sx={{ textTransform: "none" }}
        >
          {importing ? <CircularProgress size={20} color="inherit" /> : "Import Salesforce"}
        </Button>
      </Stack>

      {importError ? <Alert severity="error">{importError}</Alert> : null}
      {importSuccess ? <Alert severity="success">{importSuccess}</Alert> : null}
    </Box>
  );
}

function ChangePasswordSection({
  passwordForm,
  setPasswordForm,
  passwordError,
  passwordSuccess,
  showCurrentPassword,
  showNewPassword,
  showConfirmPassword,
  passwordFieldsReadOnly,
  onUnlockCurrentPassword,
  onUnlockNewPassword,
  onUnlockConfirmPassword,
  onToggleCurrentPasswordVisibility,
  onToggleNewPasswordVisibility,
  onToggleConfirmPasswordVisibility,
}) {
  return (
    <Box component="form" autoComplete="off" sx={{ display: "grid", gap: 1.5 }}>
      <Box
        aria-hidden="true"
        sx={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0 }}
      >
        <input type="text" name="username" autoComplete="username" tabIndex={-1} />
        <input type="password" name="password" autoComplete="current-password" tabIndex={-1} />
      </Box>
      <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
        Update the password used for your current account.
      </Typography>
      {passwordError ? <Alert severity="error">{passwordError}</Alert> : null}
      {passwordSuccess ? <Alert severity="success">{passwordSuccess}</Alert> : null}
      <TextField
        size="small"
        name="vektor42-current-password"
        label="Current Password"
        type={showCurrentPassword ? "text" : "password"}
        autoComplete="new-password"
        onFocus={onUnlockCurrentPassword}
        value={passwordForm.currentPassword}
        onChange={(e) =>
          setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
        }
        InputProps={{
          readOnly: passwordFieldsReadOnly.currentPassword,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onToggleCurrentPasswordVisibility} edge="end">
                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          autoCapitalize: "none",
          autoCorrect: "off",
          spellCheck: "false",
          "data-lpignore": "true",
          "data-form-type": "other",
        }}
      />
      <TextField
        size="small"
        name="vektor42-new-password"
        label="New Password"
        type={showNewPassword ? "text" : "password"}
        autoComplete="new-password"
        onFocus={onUnlockNewPassword}
        value={passwordForm.newPassword}
        onChange={(e) =>
          setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
        }
        InputProps={{
          readOnly: passwordFieldsReadOnly.newPassword,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onToggleNewPasswordVisibility} edge="end">
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          autoCapitalize: "none",
          autoCorrect: "off",
          spellCheck: "false",
          "data-lpignore": "true",
          "data-form-type": "other",
        }}
      />
      <TextField
        size="small"
        name="vektor42-confirm-password"
        label="Confirm New Password"
        type={showConfirmPassword ? "text" : "password"}
        autoComplete="new-password"
        onFocus={onUnlockConfirmPassword}
        value={passwordForm.confirmPassword}
        onChange={(e) =>
          setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
        }
        helperText="Enter the new password twice to confirm it."
        InputProps={{
          readOnly: passwordFieldsReadOnly.confirmPassword,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onToggleConfirmPasswordVisibility} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          autoCapitalize: "none",
          autoCorrect: "off",
          spellCheck: "false",
          "data-lpignore": "true",
          "data-form-type": "other",
        }}
      />
    </Box>
  );
}

export default function Header({
  currentUser,
  userConfiguration,
  onLogout,
  onChangePassword,
  onSaveUserConfiguration,
  showCommentNotifications = false,
  commentNotifications = { unreadCount: 0, items: [] },
  commentNotificationsLoading = false,
  onMarkAllCommentNotificationsRead,
  onCommentNotificationSelect,
  canImportSalesforce = false,
  onSalesforceImport,
  onHomeClick,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [commentNotificationsAnchor, setCommentNotificationsAnchor] = useState(null);
  const [activeSection, setActiveSection] = useState(SECTION_PROFILE);
  const [draftProfile, setDraftProfile] = useState({
    displayInitials: "",
    userProfileImage: "",
  });
  const [draftNotifications, setDraftNotifications] = useState({
    email: currentUser?.email || "",
    frequency: "WEEKLY",
  });
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFieldsReadOnly, setPasswordFieldsReadOnly] = useState({
    currentPassword: true,
    newPassword: true,
    confirmPassword: true,
  });
  const displayInitials = userConfiguration?.displayInitials?.trim() || "";

  const initials = useMemo(
    () =>
      buildAvatarInitials(
        displayInitials,
        currentUser?.first_name,
        currentUser?.last_name
      ),
    [displayInitials, currentUser?.first_name, currentUser?.last_name]
  );

  const fullName = `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim();
  const visibleSectionOptions = useMemo(
    () =>
      sectionOptions.filter(
        (section) => !section.requiresSalesforceImport || canImportSalesforce
      ),
    [canImportSalesforce]
  );

  useEffect(() => {
    setDraftProfile({
      displayInitials: userConfiguration?.displayInitials || "",
      userProfileImage: userConfiguration?.userProfileImage || "",
    });
    setDraftNotifications({
      email: currentUser?.email || "",
      frequency: userConfiguration?.notificationFrequency || "WEEKLY",
    });
  }, [
    currentUser?.email,
    userConfiguration?.displayInitials,
    userConfiguration?.userProfileImage,
    userConfiguration?.notificationFrequency,
  ]);

  useEffect(() => {
    if (!canImportSalesforce && activeSection === SECTION_SALESFORCE_IMPORT) {
      setActiveSection(SECTION_PROFILE);
    }
  }, [activeSection, canImportSalesforce]);

  const openMenu = (event) => {
    setDraftProfile({
      displayInitials: userConfiguration?.displayInitials || "",
      userProfileImage: userConfiguration?.userProfileImage || "",
    });
    setDraftNotifications({
      email: currentUser?.email || "",
      frequency: userConfiguration?.notificationFrequency || "WEEKLY",
    });
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setSaveError("");
    setSaveSuccess("");
    setPasswordError("");
    setPasswordSuccess("");
    setPasswordFieldsReadOnly({
      currentPassword: true,
      newPassword: true,
      confirmPassword: true,
    });
    setActiveSection(SECTION_PROFILE);
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setDraftProfile({
      displayInitials: userConfiguration?.displayInitials || "",
      userProfileImage: userConfiguration?.userProfileImage || "",
    });
    setDraftNotifications({
      email: currentUser?.email || "",
      frequency: userConfiguration?.notificationFrequency || "WEEKLY",
    });
    setSaveError("");
    setSaveSuccess("");
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError("");
    setPasswordSuccess("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setPasswordFieldsReadOnly({
      currentPassword: true,
      newPassword: true,
      confirmPassword: true,
    });
    setMenuAnchor(null);
  };

  const openCommentNotifications = (event) => {
    setCommentNotificationsAnchor(event.currentTarget);
  };

  const closeCommentNotifications = () => {
    setCommentNotificationsAnchor(null);
  };

  const handlePhotoUpload = async (event) => {
    const [file] = event.target.files || [];

    try {
      if (!file) {
        return;
      }
      setSaveError("");
      setSaveSuccess("");
      const userProfileImage = await buildProfileImageDataUrl(file);
      setDraftProfile((prev) => ({
        ...prev,
        userProfileImage,
      }));
    } catch (err) {
      setSaveError(err?.message || "Unable to process that image.");
    } finally {
      event.target.value = "";
    }
  };

  const handleRemovePhoto = () => {
    setSaveError("");
    setSaveSuccess("");
    setDraftProfile((prev) => ({
      ...prev,
      userProfileImage: "",
    }));
  };

  const handleSaveChanges = async () => {
    setSaveError("");
    setSaveSuccess("");

    try {
      setSavingSettings(true);
      await onSaveUserConfiguration?.({
        displayInitials: draftProfile.displayInitials,
        notificationFrequency: draftNotifications.frequency,
        userProfileImage: draftProfile.userProfileImage,
      });
      setSaveSuccess("Settings updated successfully.");
      setMenuAnchor(null);
    } catch (err) {
      setSaveError(err?.message || "Unable to update settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError("Complete all password fields before saving.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation must match.");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }

    try {
      setSavingPassword(true);
      await onChangePassword?.({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess("Password updated successfully.");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      setPasswordError(err?.message || "Unable to update password.");
    } finally {
      setSavingPassword(false);
    }
  };

  const renderActiveSection = () => {
    if (activeSection === SECTION_PROFILE) {
      return (
        <ProfileSection
          currentUser={currentUser}
        />
      );
    }

    if (activeSection === SECTION_DISPLAY_OPTIONS) {
      return (
        <DisplayOptionsSection
          initials={initials}
          profile={draftProfile}
          setProfile={setDraftProfile}
          onPhotoUpload={handlePhotoUpload}
          onRemovePhoto={handleRemovePhoto}
        />
      );
    }

    if (activeSection === SECTION_CHANGE_PASSWORD) {
      return (
        <ChangePasswordSection
          passwordForm={passwordForm}
          setPasswordForm={setPasswordForm}
          passwordError={passwordError}
          passwordSuccess={passwordSuccess}
          showCurrentPassword={showCurrentPassword}
          showNewPassword={showNewPassword}
          showConfirmPassword={showConfirmPassword}
          passwordFieldsReadOnly={passwordFieldsReadOnly}
          onUnlockCurrentPassword={() =>
            setPasswordFieldsReadOnly((prev) => ({ ...prev, currentPassword: false }))
          }
          onUnlockNewPassword={() =>
            setPasswordFieldsReadOnly((prev) => ({ ...prev, newPassword: false }))
          }
          onUnlockConfirmPassword={() =>
            setPasswordFieldsReadOnly((prev) => ({ ...prev, confirmPassword: false }))
          }
          onToggleCurrentPasswordVisibility={() =>
            setShowCurrentPassword((prev) => !prev)
          }
          onToggleNewPasswordVisibility={() =>
            setShowNewPassword((prev) => !prev)
          }
          onToggleConfirmPasswordVisibility={() =>
            setShowConfirmPassword((prev) => !prev)
          }
        />
      );
    }

    if (activeSection === SECTION_SALESFORCE_IMPORT) {
      return <SalesforceImportSection onSalesforceImport={onSalesforceImport} />;
    }

    return (
      <NotificationsSection
        notifications={draftNotifications}
        setNotifications={setDraftNotifications}
      />
    );
  };

  return (
    <AppBar
      position="fixed"
      sx={{ backgroundColor: "#ffffff", boxShadow: "0px 2px 5px rgba(0,0,0,0.1)" }}
    >
      <Container maxWidth="xl">
        <Toolbar
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "88px minmax(0, 1fr) auto",
              md: "104px minmax(0, 1fr) auto",
            },
            alignItems: "center",
            gap: 1.5,
            minHeight: { xs: 68, md: 76 },
            px: { xs: 0, md: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifySelf: "start",
              gap: { xs: 0.75, md: 1 },
            }}
          >
            <ButtonBase
              aria-label="Go to Crunchbase home"
              onClick={onHomeClick}
              sx={{
                borderRadius: 1.5,
                p: 0.25,
                "&:focus-visible": {
                  outline: "2px solid rgba(14, 165, 233, 0.65)",
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                component="img"
                src="/brokancode.png"
                alt="Brokancode"
                sx={{ height: { xs: 56, md: 74 }, display: "block" }}
              />
            </ButtonBase>
          </Box>

          <Box />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifySelf: "end",
              gap: 1,
            }}
          >
            {showCommentNotifications ? (
              <IconButton
                onClick={openCommentNotifications}
                sx={{
                  p: 0.4,
                  border: "1px solid rgba(15, 23, 42, 0.12)",
                  borderRadius: 999,
                }}
              >
                <Badge
                  color="error"
                  badgeContent={commentNotifications.unreadCount}
                  max={99}
                  invisible={commentNotifications.unreadCount === 0}
                >
                  <NotificationsNoneIcon />
                </Badge>
              </IconButton>
            ) : null}

            <IconButton
              onClick={openMenu}
              sx={{
                p: 0.25,
                border: "1px solid rgba(15, 23, 42, 0.12)",
                borderRadius: 999,
              }}
            >
              <Avatar
                src={userConfiguration?.userProfileImage || undefined}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "#e64a19",
                  fontSize: "0.95rem",
                  fontWeight: 700,
                }}
              >
                {!userConfiguration?.userProfileImage ? initials : null}
              </Avatar>
            </IconButton>

            <Box
              component="img"
              src="/pharmaron.png"
              alt="Pharmaron"
              sx={{ height: { xs: 34, md: 44 }, justifySelf: "end" }}
            />
          </Box>
        </Toolbar>
      </Container>

      <Popover
        open={Boolean(commentNotificationsAnchor)}
        anchorEl={commentNotificationsAnchor}
        onClose={closeCommentNotifications}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: { xs: "min(92vw, 360px)", md: 400 },
            mt: 1.5,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
            backgroundColor: "#f8fafc",
            display: "grid",
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
              Comment Notifications
            </Typography>
            <Button
              size="small"
              onClick={() => onMarkAllCommentNotificationsRead?.()}
              disabled={commentNotifications.unreadCount === 0}
              sx={{ textTransform: "none", fontWeight: 700, minWidth: 0, px: 0.5 }}
            >
              Mark all as read
            </Button>
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
            New comments from other users. They stay here until you mark them as read or log out.
          </Typography>
        </Box>

        <Box
          sx={{
            maxHeight: 420,
            overflowY: "auto",
            display: "grid",
            gap: 0,
            backgroundColor: "#ffffff",
          }}
        >
          {commentNotificationsLoading ? (
            <Box sx={{ px: 1.5, py: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>
                Loading notifications...
              </Typography>
            </Box>
          ) : commentNotifications.items?.length ? (
            commentNotifications.items.map((notification, index) => (
              <ButtonBase
                key={notification.id}
                onClick={() => {
                  onCommentNotificationSelect?.(notification);
                  closeCommentNotifications();
                }}
                sx={{
                  width: "100%",
                  textAlign: "left",
                  alignItems: "stretch",
                  justifyContent: "stretch",
                  px: 1.5,
                  py: 1.25,
                  borderBottom:
                    index === commentNotifications.items.length - 1
                      ? "none"
                      : "1px solid rgba(15, 23, 42, 0.06)",
                  backgroundColor: "#ffffff",
                }}
              >
                <Box sx={{ width: "100%", display: "grid", gap: 0.75 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={formatCommentNotificationContext(notification.leadContext)}
                      size="small"
                      sx={getCommentNotificationContextChipSx(notification.leadContext)}
                    />
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                      {formatCommentNotificationTimestamp(notification.createdAt)}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: "0.92rem", color: "#1e293b", fontWeight: 700 }}>
                    {notification.organizationName}
                  </Typography>

                  <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                    {notification.userId}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.88rem",
                      color: "#334155",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      wordBreak: "break-word",
                    }}
                  >
                    {notification.message}
                  </Typography>
                </Box>
              </ButtonBase>
            ))
          ) : (
            <Box sx={{ px: 1.5, py: 2.5 }}>
              <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>
                No unread comment notifications.
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>

      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: { xs: 360, md: 500 },
            mt: 1.5,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <Box sx={{ p: 2, background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "64px minmax(0, 1fr)",
              gap: 1.5,
              alignItems: "center",
              p: 1.5,
              borderRadius: 2.5,
              backgroundColor: "rgba(255,255,255,0.8)",
            }}
          >
            <Avatar
              src={userConfiguration?.userProfileImage || undefined}
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#e64a19",
                fontSize: "1.35rem",
                fontWeight: 700,
              }}
            >
              {!userConfiguration?.userProfileImage ? initials : null}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#1f2937", lineHeight: 1.15 }}
              >
                {fullName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.35, color: "#64748b", wordBreak: "break-word" }}
              >
                {currentUser?.email || ""}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "170px minmax(0, 1fr)" },
            minHeight: 360,
          }}
        >
          <Box
            sx={{
              borderRight: { md: "1px solid rgba(15, 23, 42, 0.08)" },
              borderBottom: { xs: "1px solid rgba(15, 23, 42, 0.08)", md: "none" },
              p: 1,
              display: "grid",
              gap: 0.5,
              alignContent: "start",
            }}
          >
            {visibleSectionOptions.map((section) => {
              const Icon = section.icon;
              const active = activeSection === section.id;

              return (
                <ButtonBase
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    borderRadius: 2,
                    px: 1.25,
                    py: 1,
                    gap: 1.25,
                    backgroundColor: active ? "rgba(37, 99, 235, 0.08)" : "transparent",
                    color: active ? "#1d4ed8" : "#334155",
                  }}
                >
                  <Icon fontSize="small" />
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: active ? 700 : 500 }}>
                    {section.label}
                  </Typography>
                </ButtonBase>
              );
            })}

            <Divider sx={{ my: 0.75 }} />

            <ButtonBase
              onClick={onLogout}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                borderRadius: 2,
                px: 1.25,
                py: 1,
                gap: 1.25,
                color: "#b91c1c",
              }}
            >
              <LogoutIcon fontSize="small" />
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
                Log out
              </Typography>
            </ButtonBase>
          </Box>

          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: "#1f2937" }}>
              {visibleSectionOptions.find((section) => section.id === activeSection)?.label}
            </Typography>

            {renderActiveSection()}

            {activeSection !== SECTION_CHANGE_PASSWORD &&
            activeSection !== SECTION_SALESFORCE_IMPORT &&
            saveError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveError}
              </Alert>
            ) : null}
            {activeSection !== SECTION_CHANGE_PASSWORD &&
            activeSection !== SECTION_SALESFORCE_IMPORT &&
            saveSuccess ? (
              <Alert severity="success" sx={{ mt: 2 }}>
                {saveSuccess}
              </Alert>
            ) : null}

            {activeSection !== SECTION_SALESFORCE_IMPORT ? (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={
                    activeSection === SECTION_CHANGE_PASSWORD
                      ? handleChangePassword
                      : handleSaveChanges
                  }
                  disabled={savingPassword}
                  sx={{ textTransform: "none" }}
                >
                  {activeSection === SECTION_CHANGE_PASSWORD
                      ? savingPassword
                      ? "Updating..."
                      : "Update Password"
                    : savingSettings
                    ? "Saving..."
                    : "Save Changes"}
                </Button>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Popover>
    </AppBar>
  );
}
